import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {pathToURL} from 'BenchlingUtil';
import ErrorBadge from 'procedures/design/error-panel/ErrorBadge';
import {MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import {getProcedureRunIcon} from 'procedures/design/procedureIconUtils';
import * as ProcedureRunPlanDAO from 'procedures/network/procedureRunPlanDAO';
import {StudyDesignTabEditState} from 'studies/typed/shared/procedure-runs/design-tabs/StudyDesignTabSavingButtons';
import {useLinkToExistingUnitOperationsPlanAction} from 'studies/typed/utils/ui/useLinkToExistingUnitOperationsPlanAction';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import Header, {type ActionItem} from 'ui-library/headers/Header';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {CreatedAt, getProcedureRunPlanHeaderButtons, type StudyHeaderFields} from './procedureRunHeaderUtils';
import {useProcedureRunPlanContext} from './StudyOverviewContexts';

export interface ProcedureRunPlanHeaderProps {
  study: StudyHeaderFields;
  procedureRunPlanId: string;
  procedureRunPlanName: string;
  procedureRunPlanDescription?: string;
  procedureRunPlanCreatedAt?: string;
  isEditable: boolean;
  shouldDisableCreate: boolean;
  onCreateRun: () => void;
  onLinksSaved: () => void;
  refetch: () => void;
  onStudyCrumbClick: () => void;
}

export default function ProcedureRunPlanHeader({
  study,
  procedureRunPlanId,
  procedureRunPlanName,
  procedureRunPlanDescription,
  procedureRunPlanCreatedAt,
  isEditable,
  shouldDisableCreate,
  onCreateRun,
  onLinksSaved,
  refetch,
  onStudyCrumbClick,
}: ProcedureRunPlanHeaderProps): JSX.Element {
  const {classes} = useStyles();
  const {planDesignActions} = useProcedureRunPlanContext();
  const studyType = study.studySchema.studyType;
  const runLabel = getProcedureRunUIStringForStudyType(studyType);
  const planErrors = planDesignActions?.errors ?? [];
  const hasPlanErrors = planErrors.length > 0;

  const linkAction = useLinkToExistingUnitOperationsPlanAction({
    studyId: study.id,
    procedureRunPlanId,
    procedureRunPlanName,
    studyType,
    onLinksSaved,
  });
  const copyLinkAction: ActionItem = {
    icon: 'link',
    name: `Copy ${runLabel} link`,
    onSelect: () =>
      copyToClipboard(pathToURL(`${study.editUrl}?runId=${procedureRunPlanId}`), {
        showToastOnSuccess: true,
        toastText: `Copied ${runLabel} link.`,
      }),
  };
  const exportAuditAction: ActionItem = {
    icon: 'audit-trail',
    name: 'Export audit log',
    onSelect: () => {
      exportAuditLogs([procedureRunPlanId]);
    },
  };

  const [planDescription, setPlanDescription] = useState(procedureRunPlanDescription ?? '');
  useEffect(() => {
    setPlanDescription(procedureRunPlanDescription ?? '');
  }, [procedureRunPlanDescription, procedureRunPlanId]);

  const descriptionEditor = (
    <div className={classes.descriptionEditor}>
      <EditableTextField
        requestChange={async (updated: string | number) => {
          const description = updated as string;
          setPlanDescription(description);
          await ProcedureRunPlanDAO.updateProcedureRunPlan({
            id: procedureRunPlanId,
            description,
            studyId: study.id,
          });
          refetch();
        }}
        isDisabled={!isEditable}
        emptyValueAllowed={true}
        value={planDescription}
        maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
        type={TextFieldType.TEXTAREA}
        autoSize={true}
        textPlaceholder={`Add ${runLabel} plan description`}
      />
    </div>
  );

  const miscItems = [<CreatedAt key="createdAt" createdAt={procedureRunPlanCreatedAt} />];
  if (hasPlanErrors) {
    miscItems.unshift(
      <ErrorBadge key="errorBadge" errors={planErrors} title={`Fix all errors to create this ${runLabel}`} />
    );
  }

  const withActionIconColor = (item: ActionItem): ActionItem => ({...item, className: classes.actionIcon});
  const actionIconButtons = [linkAction, copyLinkAction, exportAuditAction].map((item) =>
    withActionIconColor(item)
  );

  return (
    <Header
      title={procedureRunPlanName}
      dataTestAttrs={{component: 'ProcedureRunPlanHeader', element: 'root'}}
      crumbs={[
        {
          label: study.name,
          dataTestAttrs: {component: 'ProcedureRunPlanHeader', element: 'studyBreadcrumb'},
          onClick: () => {
            onStudyCrumbClick();
          },
        },
      ]}
      icon={getProcedureRunIcon(getProcedureTypeForStudyType(studyType))}
      classNames={{body: classes.body}}
      badges={[{text: 'Plan', color: 'gray'}]}
      isCollapsed={false}
      fullTextButtons={getProcedureRunPlanHeaderButtons({
        hasUnsavedPlanChanges: planDesignActions?.hasUnsavedChanges ?? false,
        isSavingPlan: planDesignActions?.editState === StudyDesignTabEditState.SAVING,
        isSaveDisabled: planDesignActions?.isSaveDisabled ?? true,
        saveDisabledTooltip: planDesignActions?.saveDisabledTooltip,
        hasPlanErrors,
        shouldDisableCreate: shouldDisableCreate || planDesignActions == null,
        onSavePlan: planDesignActions?.onSave ?? noop,
        onCreateRun,
      })}
      actionIconButtons={actionIconButtons}
      miscItems={miscItems}
    >
      {descriptionEditor}
    </Header>
  );
}

const useStyles = createStyles((theme) => ({
  body: {
    position: 'relative',
    // Keep the header shadow above the flowchart's left and right sidepanels.
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX + 1,
  },
  actionIcon: {
    color: theme.colors.gray[6],
  },
  descriptionEditor: {
    width: '100%',
  },
}));
